<script lang="ts" setup>
import { onMounted, ref, toRefs, watch, Ref, inject, computed } from 'vue';
import InputNumber from 'primevue/inputnumber';
import { TRIAL_GOAL_PARAMS } from '@/enum';
import { TrialGoalInterface } from '@/types';
import Chip from 'primevue/chip';

interface PropsInterface {
  attendeeId: number;
  trial: TrialGoalInterface;
  disabled: boolean;
  displayPastEvents: boolean;
}

const props = defineProps<PropsInterface>();

//inject the function from the grandparent DocEventModal to skip all the emit injections
const updateTrialGoal = inject('updateTrialGoal') as (
  checkboxArray: number[],
  max: number,
  id: number,
  trialId: number,
  trialCount: number,
  percentage: number,
) => void;
const { trial, attendeeId, displayPastEvents } = toRefs(props);
const selectedValueArray: Ref<number[]> = ref([]);
const totalValueArray: Ref<number[]> = ref(Array(trial.value.total_trials).fill(0));
const updateDisabled: Ref<boolean> = ref(true);
const maxValue: Ref<number> = ref(trial.value.total_trials);
const percentage: Ref<number> = ref(trial.value.percentage || 0);
const trialCount: Ref<number> = ref(trial.value.trials);

onMounted(() => {
  setSelectedValueArray();
  setTrialTotal();
  //TODO: skipping this until we figure out if we need it
  //getTrialBitTotal();
});

/**
 * set up the selectedValueArray based on the incoming checkbox_order array
 */
const setSelectedValueArray = () => {
  if (trial.value.checkbox_order) {
    trial.value.checkbox_order?.forEach((item, index) => {
      if (item === 1) {
        selectedValueArray.value.push(index);
      }
    });
  }
};
/**
 * TODO: is this necessary? there is no bit table in the DB
 */
// const getTrialBitTotal = () => {
//   let bitfieldDefault = 1;
//   let bitfield = 0;

//   selectedValueArray.value.forEach((trial) => {
//     bitfield |= bitfieldDefault;

//     bitfieldDefault <<= 1;
//   });
//   setTrialTotal();
// };

/**
 * Set the calculated trial percentage and trial total.
 *
 * @return {Void}
 */
const setTrialTotal = () => {
  trialCount.value = selectedValueArray.value.filter((item) => item != 0).length;
  percentage.value = Math.round((trialCount.value / trial.value.total_trials) * 100);
};

/**
 * handle changing the max size and checkbox selections
 */
const handleChange = () => {
  mergeArrays();
  setTrialTotal();
  update();
};

/**
 * merge the selected values indexes into the total value array
 *
 */
const mergeArrays = () => {
  //set all the totalValueArray items to unchecked
  totalValueArray.value = Array(trial.value.total_trials).fill(0);

  //if there are any selected values greater than the max then filter them out
  selectedValueArray.value = selectedValueArray.value.filter((val) => val <= trial.value.total_trials);

  //each selected value in the array is actually the INDEX position-1 to set to 1 (checked)
  selectedValueArray.value.forEach((val) => {
    totalValueArray.value[val] = 1;
  });
};

watch(() => trial.value.total_trials, handleChange);

const decrementMax = () => {
  if (maxValue.value === TRIAL_GOAL_PARAMS.MIN_ALLOWED) return;
  maxValue.value--;
  update();
};

const incrementMax = () => {
  if (maxValue.value === TRIAL_GOAL_PARAMS.MAX_ALLOWED) return;
  maxValue.value++;
  update();
};

/**
 * handle the text input update for max
 */
const handleInputUpdate = () => {
  if (isOutOfBounds.value) return;
  update();
  updateDisabled.value = true;
};

/**
 * update the grandparent
 */
const update = () =>
  updateTrialGoal(
    totalValueArray.value,
    maxValue.value,
    attendeeId.value,
    trial.value.treatment_goal_id,
    trialCount.value,
    percentage.value,
  );

const isOutOfBounds = computed(
  () => maxValue.value > TRIAL_GOAL_PARAMS.MAX_ALLOWED || maxValue.value < TRIAL_GOAL_PARAMS.MIN_ALLOWED,
);

const enableUpdate = () => (updateDisabled.value = false);

const cancelUpdate = () => {
  maxValue.value = trial.value.total_trials;
  updateDisabled.value = true;
};
</script>

<template>
  <div class="trial-checkboxes w-full flex justify-content-between align-items-center">
    <div class="flex w-full" :class="displayPastEvents ? 'grid' : ''">
      <div class="flex align-items-center" :class="displayPastEvents ? 'flex-order-0 col-6 xl:col-3' : ''">
        <InputNumber
          v-model="maxValue"
          :disabled="updateDisabled"
          :max="TRIAL_GOAL_PARAMS.MAX_ALLOWED"
          :min="TRIAL_GOAL_PARAMS.MIN_ALLOWED"
          class="trial-checkboxes__max"
        />
        <button
          v-if="updateDisabled"
          :disabled="disabled"
          class="button edit border-noround pr-2 m-0"
          @click="enableUpdate"
        >
          <Icon name="edit" class="text-base" />
        </button>
        <div v-if="!updateDisabled" class="trial-checkboxes__edit flex flex-row">
          <button class="button close border-round-sm w-1rem h-1rem font-bold" @click="cancelUpdate">
            <Icon name="close" />
          </button>
          <button class="button check border-round-sm w-1rem h-1rem font-bold" @click="handleInputUpdate">
            <Icon name="check" />
          </button>
        </div>
        <div class="divider">&nbsp;</div>
      </div>
      <div
        class="flex flex-1 align-items-center"
        :class="
          displayPastEvents
            ? 'flex-order-2 col-12 justify-content-center xl:flex-order-1 xl:justify-content-start xl:col-7'
            : ''
        "
      >
        <div class="px-1 increment-container">
          <button class="subtract button" :disabled="!updateDisabled || disabled" @click="decrementMax">
            <span class="material-icons-round">remove</span>
          </button>
        </div>
        <div class="checkbox-container flex-wrap">
          <Checkbox
            v-for="number in trial.total_trials"
            :key="number"
            v-model="selectedValueArray"
            :disabled="disabled"
            :value="number"
            class="border-noround"
            @change="handleChange"
          />
        </div>
        <div class="px-1 increment-container">
          <button class="add button" :disabled="!updateDisabled || disabled" @click="incrementMax">
            <span class="material-icons-round">add</span>
          </button>
        </div>
      </div>

      <div
        class="flex align-self-center justify-content-end"
        :class="displayPastEvents ? 'flex-order-1 col-6 xl:flex-order-2 xl:col-2' : ''"
      >
        <Chip class="total font-bold justify-content-center">{{ percentage }}%</Chip>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.trial-checkboxes {
  display: flex;
  flex-direction: row;
  align-items: center;
  .trial-checkboxes__max {
    border-radius: 6px;

    &:disabled {
      border-color: $darkLight;
    }
  }

  .button.edit {
    background: $grayLight;
    width: 1.5rem;
    height: 1.5rem;
    i {
      color: $grayDark;
    }
  }
  .trial-checkboxes__edit {
    .button {
      border-radius: 4px;
      background-color: white;
      &.check {
        i {
          color: var(--primary-color-dark);
        }
      }
      &.close {
        i {
          color: $errorColor;
        }
      }
    }
  }
  .divider {
    background-color: $grayMedium;
    width: 3px;
    height: 24px;
    border-radius: 2px;
  }
  :deep(.p-inputtext) {
    width: 60px;
    margin-right: 0.5rem;
  }

  .checkbox-container {
    display: flex;
    flex-direction: row;
    align-items: space-between;
    background-color: $white;
    padding: 5px;
    border-radius: 3px;
    max-width: 415px;

    :deep(.p-checkbox) {
      height: 14px;
      width: 14px;
      margin: 3px;

      .p-checkbox-box {
        height: 13.5px;
        width: 13.5px;
        border: 2px var(--text-color-secondary) solid;
        border-radius: 2px;
        .p-checkbox-icon {
          font-size: 9px;
          font-weight: 600;
        }

        &.p-highlight {
          background-color: #8bcfcf;
          border-color: #8bcfcf;
        }
      }
    }
  }
  .total {
    background: var(--primary-color-dark);
    padding: 0px 3px;
    color: white;
    font-size: 10px;
    width: 40px;
    height: 18px;
  }
  .button {
    border-radius: 7.5px;
    width: 15px;
    height: 15px;
    display: flex;
    background-color: var(--primary-color-dark);
    border: none;
    align-items: center;
    justify-content: center;
    padding: 0;
    margin: 0 6px;
    cursor: pointer;

    span {
      margin: 0;
      font-size: 11px;
      color: $white;
      font-weight: 600;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
  .increment-container {
    margin: 0 6px;

    .button {
      margin: 0;
    }
  }
}
</style>
